import React, { useMemo, useState } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import DialogModal from "../../components/Modal/DialogModal";
import ChatStatusBanner from "../../components/Chat/ChatStatusBanner";
import MainLayout from "../../layouts/MainLayout";

const PRODUCT_FEATURES = [
  {
    title: "Legal research, without query gymnastics",
    description:
      "Ask legal questions in plain English and move from issue spotting to usable analysis faster.",
  },
  {
    title: "Draft with case-law context",
    description:
      "Use the workspace to summarise material, draft content, and refine legal writing with stronger context.",
  },
  {
    title: "Built for Sri Lankan legal work",
    description:
      "A focused workspace tailored for legal professionals, not a generic public chatbot.",
  },
];

const INCLUDED_ITEMS = [
  "Monthly access to chat.paralegal.lk",
  "AI-assisted legal research workflows",
  "Drafting and summarisation workspace",
  "Ongoing platform improvements and feature updates",
];

const GROUP_PLAN_HIGHLIGHTS = [
  "Multi-user access for legal teams",
  "Coordinated onboarding support",
  "Subscription structure aligned to team usage",
];

const STATUS_OPTIONS = new Set([
  "success",
  "pending",
  "failed",
  "already_subscribed",
]);

const chatUrl =
  import.meta.env.VITE_CHAT_DOMAIN_URL || "https://chat.paralegal.lk";
const chatDomainLabel = chatUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");

const Chat = () => {
  const { state, signIn } = useAuthContext();
  const [searchParams] = useSearchParams();
  const [modalState, setModalState] = useState(null);

  const status = useMemo(() => {
    const value = searchParams.get("status");
    return STATUS_OPTIONS.has(value) ? value : null;
  }, [searchParams]);

  const handleStartSubscription = () => {
    // TODO: Replace this mocked branch with a server-created checkout session.
    // TODO: The server must derive identity from a validated Asgardeo `sub`,
    // not from client-controlled fields such as email.
    if (!state.isAuthenticated) {
      setModalState("auth_required");
      return;
    }

    setModalState("coming_soon");
  };

  const handleLogin = () => {
    sessionStorage.setItem(
      "redirectAfterLogin",
      `${window.location.pathname}${window.location.search}`
    );
    signIn();
  };

  const modalConfig =
    modalState === "auth_required"
      ? {
          title: "Login required",
          description: "Please log in to continue to checkout.",
          actions: [
            { label: "Close", onClick: () => setModalState(null) },
            { label: "Log in", onClick: handleLogin, variant: "primary" },
          ],
        }
      : modalState === "coming_soon"
      ? {
          title: "Payment gateway coming soon",
          description:
            "Checkout will open shortly. No payment is collected at this time.",
          actions: [
            { label: "Close", onClick: () => setModalState(null), variant: "primary" },
          ],
        }
      : null;

  return (
    <MainLayout>
      <Helmet>
        <title>Chat | paralegal.lk</title>
        <meta
          name="description"
          content="Subscribe to Paralegal’s dedicated chat workspace for AI-assisted legal research, drafting, and analysis."
        />
        <link rel="canonical" href="https://www.paralegal.lk/chat" />
      </Helmet>

      <div className="w-full max-w-6xl px-6 py-10 text-pl-text-primary md:px-10 md:py-14">
        <ChatStatusBanner status={status} />

        <section className="rounded-[24px] border border-pl-border bg-pl-bg-elevated p-8 shadow-pl-md md:p-12">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-start">
            <div>
              <p className="text-12 font-semibold uppercase tracking-[0.22em] text-pl-accent">
                Subscription Workspace for Legal Professionals
              </p>
              <h1 className="mt-4 max-w-3xl font-display text-36 font-semibold leading-tight md:text-[52px]">
                AI legal research and drafting for Sri Lankan legal practice.
              </h1>
              <p className="mt-5 max-w-2xl text-16 leading-8 text-pl-text-secondary md:text-18">
                <a
                  href={chatUrl}
                  className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
                >
                  {chatDomainLabel}
                </a>{" "}
                is Paralegal’s dedicated subscription workspace for deeper
                legal research, drafting, and analysis. Work faster from first
                question to final written output in one focused environment.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <button
                  type="button"
                  onClick={handleStartSubscription}
                  className="rounded-pl bg-pl-accent px-6 py-3 text-15 font-semibold text-white transition-colors hover:bg-pl-accent-hover"
                  aria-label="Start monthly chat subscription"
                >
                  Start Subscription
                </button>
                <Link
                  to="/about"
                  className="rounded-pl border border-pl-border bg-pl-bg px-6 py-3 text-15 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle"
                >
                  Learn about Paralegal
                </Link>
              </div>

              <p className="mt-4 text-14 font-semibold text-pl-text-primary">
                Access is available to registered subscribers only.
              </p>
              <p className="mt-2 max-w-2xl text-14 leading-7 text-pl-text-secondary">
                Sign in and subscribe to access{" "}
                <a
                  href={chatUrl}
                  className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
                >
                  {chatDomainLabel}
                </a>
                . Already subscribed? Sign in with your Paralegal account.
              </p>
              <p className="mt-4 text-13 text-pl-text-tertiary">
                Checkout will open shortly. No payment is collected at this
                time.
              </p>
            </div>

            <aside className="rounded-[20px] border border-pl-border bg-pl-bg-subtle p-6 shadow-pl-sm">
              <p className="text-12 font-semibold uppercase tracking-[0.18em] text-pl-accent">
                Monthly Plan
              </p>
              <h2 className="mt-3 font-display text-24 font-semibold text-pl-text-primary">
                Paralegal Chat Pro
              </h2>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-36 font-semibold">Rs. 20,000</span>
                <span className="pb-1 text-15 text-pl-text-secondary">/ month</span>
              </div>
              <p className="mt-4 text-14 leading-7 text-pl-text-secondary">
                One straightforward subscription tier designed for legal
                professionals and legal teams.
              </p>

              <ul className="mt-6 space-y-3">
                {INCLUDED_ITEMS.map((item) => (
                  <li key={item} className="flex gap-3 text-14 leading-6 text-pl-text-primary">
                    <span
                      className="mt-[6px] h-2.5 w-2.5 rounded-full bg-pl-accent"
                      aria-hidden="true"
                    />
                    <span>
                      {item === "Monthly access to chat.paralegal.lk" ? (
                        <>
                          Monthly access to{" "}
                          <a
                            href={chatUrl}
                            className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
                          >
                            {chatDomainLabel}
                          </a>
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={handleStartSubscription}
                className="mt-8 w-full rounded-pl bg-pl-accent px-6 py-3 text-15 font-semibold text-white transition-colors hover:bg-pl-accent-hover"
              >
                Start Subscription
              </button>

              <p className="mt-4 text-12 leading-6 text-pl-text-tertiary">
                By continuing, you agree to our{" "}
                <Link to="/terms" className="text-pl-accent hover:underline">
                  Terms
                </Link>
                ,{" "}
                <Link to="/privacy" className="text-pl-accent hover:underline">
                  Privacy Policy
                </Link>
                , and{" "}
                <Link to="/refund" className="text-pl-accent hover:underline">
                  Refund Policy
                </Link>
                .
              </p>
            </aside>
          </div>
        </section>

        <section className="mt-8 rounded-[24px] border border-pl-border bg-pl-bg-elevated p-8 shadow-pl-md md:p-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr] lg:items-start">
            <div>
              <h2 className="font-display text-28 font-semibold text-pl-text-primary md:text-36">
                Need access for your firm or team?
              </h2>
              <p className="mt-4 max-w-3xl text-16 leading-8 text-pl-text-secondary">
                For chambers, law firms, and legal teams that need multi-user
                access, shared onboarding, and tailored support, choose a group
                subscription.
              </p>
            </div>

            <aside className="rounded-[20px] border border-pl-border bg-pl-bg-subtle p-6 shadow-pl-sm">
              <h3 className="font-display text-24 font-semibold text-pl-text-primary">
                Group Subscription
              </h3>
              <p className="mt-4 text-14 leading-7 text-pl-text-secondary">
                Contact us for team pricing and onboarding tailored to your
                organization.
              </p>

              <ul className="mt-6 space-y-3">
                {GROUP_PLAN_HIGHLIGHTS.map((item) => (
                  <li key={item} className="flex gap-3 text-14 leading-6 text-pl-text-primary">
                    <span
                      className="mt-[6px] h-2.5 w-2.5 rounded-full bg-pl-accent"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <a
                href="mailto:admin@paralegal.lk?subject=Group%20Subscription%20Inquiry"
                className="mt-8 inline-flex w-full items-center justify-center rounded-pl border border-pl-accent bg-pl-bg-elevated px-6 py-3 text-15 font-semibold text-pl-accent transition-colors hover:bg-pl-accent-subtle"
              >
                Contact Us
              </a>

              <p className="mt-4 text-12 leading-6 text-pl-text-tertiary">
                Prefer to discuss requirements first? Contact us for a tailored
                plan.
              </p>
            </aside>
          </div>
        </section>

        <section className="mt-8 grid gap-5 md:grid-cols-3">
          {PRODUCT_FEATURES.map((feature) => (
            <article
              key={feature.title}
              className="rounded-[20px] border border-pl-border bg-pl-bg-elevated p-6 shadow-pl-sm"
            >
              <h2 className="font-display text-24 font-semibold text-pl-text-primary">
                {feature.title}
              </h2>
              <p className="mt-3 text-15 leading-7 text-pl-text-secondary">
                {feature.description}
              </p>
            </article>
          ))}
        </section>
      </div>

      <DialogModal
        isOpen={Boolean(modalConfig)}
        onClose={() => setModalState(null)}
        title={modalConfig?.title}
        description={modalConfig?.description}
        actions={modalConfig?.actions}
      />
    </MainLayout>
  );
};

export default Chat;
